// Progression, persisted in localStorage. Pure data — no DOM, no canvas.
import type { LevelDef } from '../sim/index.ts';

const KEY = 'qs.progress.v2';
/** v1 stored `best` as a count of arrivals; v2 stores it as a score. */
const KEY_V1 = 'qs.progress.v1';

export interface Progress {
  /** level ids the player has unlocked (cleared, or the campaign's first level) */
  unlocked: string[];
  /** best score reached per level id, kept even across a loss */
  best: Record<string, number>;
}

function defaultProgress(levels: readonly LevelDef[]): Progress {
  return { unlocked: levels.length ? [levels[0].id] : [], best: {} };
}

function parse(raw: string): Progress | null {
  const parsed: unknown = JSON.parse(raw);
  if (!parsed || typeof parsed !== 'object' || !Array.isArray((parsed as Progress).unlocked)) return null;
  const p = parsed as Progress;
  const best = p.best && typeof p.best === 'object' ? p.best : {};
  return { unlocked: p.unlocked.filter(id => typeof id === 'string'), best };
}

export function loadProgress(levels: readonly LevelDef[]): Progress {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return parse(raw) ?? defaultProgress(levels);
    // A returning player from before scoring: the cities they earned are
    // theirs to keep, but a v1 "best" counted arrivals and would read as a
    // derisory score against a baseline in the hundreds. Carry the unlocks,
    // drop the numbers, and let them be set again on the next run.
    const legacy = localStorage.getItem(KEY_V1);
    if (!legacy) return defaultProgress(levels);
    const old = parse(legacy);
    return old ? { unlocked: old.unlocked, best: {} } : defaultProgress(levels);
  } catch {
    return defaultProgress(levels); // private browsing / storage disabled
  }
}

export function saveProgress(p: Progress): void {
  try { localStorage.setItem(KEY, JSON.stringify(p)); } catch { /* quota / private mode — progress just won't persist */ }
}

/** `dev=1` unlocks the whole campaign for testing, without touching stored progress. */
export function isUnlocked(p: Progress, levelId: string, dev: boolean): boolean {
  return dev || p.unlocked.includes(levelId);
}

export function unlockLevel(p: Progress, levelId: string): Progress {
  if (p.unlocked.includes(levelId)) return p;
  return { ...p, unlocked: [...p.unlocked, levelId] };
}

/** Record a finished run's score if it beats what this city has seen before. */
export function recordScore(p: Progress, levelId: string, points: number): Progress {
  const prevBest = p.best[levelId] ?? 0;
  if (points <= prevBest) return p;
  return { ...p, best: { ...p.best, [levelId]: points } };
}
